import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AddOrdersModal from './AddOrders';

const sortableFields = {
  Client: 'Client',
  UserName: 'UserName',
  Date: 'Date',
  Value: 'Value',
};

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({
    clientName: '',
    userName: '',
    orderStatus: '',
    orderValue: '',
    orderDate: '',
    rangeType: '',
    startDate: '',
    endDate: '',
    month: '',
  });
  const [totalOrderValue, setTotalOrderValue] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null); // for edit
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ sortKey: 'createdAt', sortOrder: 'desc' });

  const userId = JSON.parse(localStorage.getItem('user'))?._id;

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}get-orders/${userId}`, {
        params: {
          ...filters,
          userId,
          page: pagination.page,
          limit: pagination.limit,
          sortKey: sortConfig.sortKey,
          sortOrder: sortConfig.sortOrder,
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
      });

      setOrders(res.data.Orders);
      setTotalOrderValue(res.data.totalOrderValue || 0);
      setPagination((prev) => ({ ...prev, totalPages: res.data.totalPages }));
    } catch (err) {
      console.error('Error fetching orders:', err);
    }
  };

  useEffect(() => {
    // Adjust date range filters for weekly/monthly etc.
    if (filters.rangeType === 'weekly' && filters.orderDate) {
      const date = new Date(filters.orderDate);
      const start = new Date(date);
      const end = new Date(date);
      end.setDate(date.getDate() + 6);

      setFilters((prev) => ({
        ...prev,
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      }));
    }

    if (filters.rangeType === 'monthly' && filters.month) {
      const [year, month] = filters.month.split('-');
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0);

      setFilters((prev) => ({
        ...prev,
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      }));
    }
  }, [filters.orderDate, filters.month]);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, sortConfig]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleFilter = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchOrders();
  };

  const clearFilters = () => {
    setFilters({
      clientName: '',
      userName: '',
      orderStatus: '',
      orderValue: '',
      orderDate: '',
      rangeType: '',
      startDate: '',
      endDate: '',
      month: '',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
    // Reset sorting optionally
    setSortConfig({ sortKey: 'createdAt', sortOrder: 'desc' });
    fetchOrders();
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}delete-order/${orderId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` }
        });
        fetchOrders(); // Refresh list
      } catch (err) {
        alert("Failed to delete order.");
        console.error(err);
      }
    }
  };

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  // Sorting click handler
  const handleSort = (key) => {
    if (sortConfig.sortKey === key) {
      // Toggle sort order
      setSortConfig({
        sortKey: key,
        sortOrder: sortConfig.sortOrder === 'asc' ? 'desc' : 'asc',
      });
    } else {
      // New sort key default ascending
      setSortConfig({ sortKey: key, sortOrder: 'asc' });
    }
    // Reset to page 1 on sorting change
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Render sort arrow icons
  const renderSortArrow = (key) => {
    if (sortConfig.sortKey !== key) return null;
    return sortConfig.sortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div className="p-4">
      {/* Top Controls */}
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => {
            setSelectedOrder(null); // Clear previous selection
            setShowModal(true);
          }}
          className="bg-green-600 text-white px-4 py-2 rounded"
        >
          + Add Order
        </button>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <input name="clientName" onChange={handleChange} value={filters.clientName} className="border p-2" placeholder="Client Name" />
        <input name="userName" onChange={handleChange} value={filters.userName} className="border p-2" placeholder="User Name" />
        <select name="orderStatus" onChange={handleChange} value={filters.orderStatus} className="border p-2">
          <option value="">All Statuses</option>
          <option value="Order Received">Order Received</option>
          <option value="PI Sent">PI Sent</option>
          <option value="Payment Pending">Payment Pending</option>
          <option value="Dispatch Clearance Pending">Dispatch Clearance Pending</option>
          <option value="Not in Stock">Not in Stock</option>
          <option value="Order Complete">Order Complete</option>
          <option value="Part Clearance Received">Part Clearance Received</option>
          <option value="Other">Other</option>
        </select>

        <select name="rangeType" onChange={handleChange} className="border p-2" value={filters.rangeType}>
          <option value="">Select Date Range</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="custom">Custom</option>
        </select>

        {filters.rangeType === 'daily' && (
          <input type="date" name="orderDate" onChange={handleChange} className="border p-2" />
        )}
        {filters.rangeType === 'weekly' && (
          <input type="date" name="orderDate" onChange={handleChange} className="border p-2" />
        )}
        {filters.rangeType === 'monthly' && (
          <input type="month" name="month" onChange={handleChange} className="border p-2" />
        )}
        {filters.rangeType === 'custom' && (
          <>
            <input type="date" name="startDate" onChange={handleChange} className="border p-2" />
            <input type="date" name="endDate" onChange={handleChange} className="border p-2" />
          </>
        )}

        <button
          onClick={handleFilter}
          className="bg-blue-600 text-white px-4 py-2 col-span-2 md:col-span-1 rounded"
        >
          Filter
        </button>
        <button
          onClick={clearFilters}
          className="bg-gray-500 text-white px-4 py-2 rounded w-full"
        >
          Clear
        </button>
      </div>

      {/* Total Order Value */}
      <div className="mb-2 font-semibold">Total Order Value: ₹ {totalOrderValue.toLocaleString('en-IN')}</div>

      {/* Order Table */}
      <table className="w-full border text-sm">
        <thead>
          <tr className="bg-gray-200 cursor-pointer select-none">
            <th className="p-2 border" onClick={() => handleSort(sortableFields.UserName)}>
              User{renderSortArrow(sortableFields.UserName)}
            </th>
            <th className="p-2 border" onClick={() => handleSort(sortableFields.Client)}>
              Client{renderSortArrow(sortableFields.Client)}
            </th>
            <th className="p-2 border" onClick={() => handleSort(sortableFields.Date)}>
              Date{renderSortArrow(sortableFields.Date)}
            </th>
            <th className="p-2 border" onClick={() => handleSort(sortableFields.Value)}>
              Value{renderSortArrow(sortableFields.Value)}
            </th>
            <th className="p-2 border">Status</th>
            <th className="p-2 border">Comment</th>
            <th className="p-2 border">Action</th>
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 && (
            <tr>
              <td colSpan="7" className="text-center p-4">
                No orders found.
              </td>
            </tr>
          )}
          {orders.map((order) => (
            <tr key={order._id}>
              <td className="p-2 border">{order.UserName}</td>
              <td className="p-2 border">{order.Client}</td>
              <td className="p-2 border">{order.Date ? new Date(order.Date).toLocaleDateString() : '-'}</td>
              <td className="p-2 border">₹{order.Value.toLocaleString()}</td>
              <td className="p-2 border">{order.Status}</td>
              <td className="p-2 border">{order.Comment}</td>
              <td className="p-2 border flex gap-4">
                <span
                  className="text-[12px] underline"
                  onClick={() => handleEditOrder(order)}
                >
                  Edit
                </span>
                <span
                  className="text-[12px] text-red-600 underline"
                  onClick={() => handleDeleteOrder(order._id)}
                >
                  Delete
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-4">
        <button
          disabled={pagination.page <= 1}
          onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
          className="px-4 py-2 bg-gray-400 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <button
          disabled={pagination.page >= pagination.totalPages}
          onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
          className="px-4 py-2 bg-gray-400 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <AddOrdersModal
          order={selectedOrder}
          onClose={() => {
            setShowModal(false);
            setSelectedOrder(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setSelectedOrder(null);
            fetchOrders();
          }}
        />
      )}
    </div>
  );
};

export default OrderList;
